/**
 * MiniNode Component
 *
 * Minimap tile: optional 1px top line when a primary tag exists; body = mission state
 * (complete / excluded / blocked / locked / active / available — original palette).
 */

import React, { memo } from 'react';
import {
  getMilestoneAnchor,
  getMilestoneJunctionCenter,
  getMilestoneLabelSide,
} from '../../../utils/milestoneUtils';
import { MilestoneFlagIcon } from '../../MilestoneFlagIcon';

function getBodyStyle({
  isComplete,
  isExcluded,
  isLocked,
  isBlocked,
  isActive,
  isAvailable,
}) {
  if (isComplete) {
    return 'rgba(34, 197, 94, 0.1)';
  }
  /* Any excluded / non-path node — red (including locked on a dead branch) */
  if (isExcluded) {
    return 'rgba(239, 68, 68, 0.22)';
  }
  if (isBlocked) {
    return 'rgba(220, 38, 38, 0.2)';
  }
  if (isLocked) {
    return 'rgba(251, 191, 36, 0.14)';
  }
  /* Cyan-leaning sky (near in-progress status accent, livelier than pure blue) */
  if (isActive) {
    return 'rgba(14, 165, 233, 0.66)';
  }
  if (isAvailable) {
    return 'rgba(110, 130, 165, 0.72)';
  }
  return 'rgba(100, 122, 158, 0.68)';
}

const MiniNode = memo(
  ({
    task,
    pos,
    dims,
    scale,
    boundsMinX,
    boundsMinY,
    primaryTagColor,
    isAvailable,
    isActive,
    isComplete,
    isLocked,
    isExcluded,
    isBlocked,
    isMilestoneReached = false,
    nodePositions = null,
    nodeDimensions = null,
  }) => {
    const shrink = 1;
    const miniX = (pos.x - boundsMinX) * scale + shrink;
    const miniY = (pos.y - boundsMinY) * scale + shrink;

    if (task.nodeType === 'note') {
      return (
        <div
          className="flowchart-minimap__note"
          style={{
            transform: `translate(${miniX}px, ${miniY}px)`,
            width: Math.max(dims.width * scale - shrink * 2, 3),
            height: Math.max(dims.height * scale - shrink * 2, 2),
            background: 'rgba(240, 196, 138, 0.55)',
            borderRadius: 2,
          }}
          aria-hidden
        />
      );
    }

    if (task.nodeType === 'milestone') {
      const anchor = getMilestoneAnchor(task);
      const hostPos = anchor?.hostTaskId ? nodePositions?.[anchor.hostTaskId] : null;
      const hostDims = anchor?.hostTaskId ? nodeDimensions?.[anchor.hostTaskId] : null;
      const labelSide = getMilestoneLabelSide(pos, dims, hostPos, hostDims);
      const junction = getMilestoneJunctionCenter(pos, dims, labelSide);

      const flagPx = Math.max(
        5,
        Math.round(Math.max(7, Math.min(11, Math.round(6 + scale * 90))) * 0.65),
      );

      const flagX = (junction.x - boundsMinX) * scale - flagPx / 2;
      const flagY = (junction.y - boundsMinY) * scale - flagPx / 2;

      return (
        <div
          className={[
            'flowchart-minimap__milestone',
            isMilestoneReached && 'flowchart-minimap__milestone--reached',
          ].filter(Boolean).join(' ')}
          style={{
            transform: `translate(${flagX}px, ${flagY}px)`,
            width: flagPx,
            height: flagPx,
          }}
          aria-hidden
        >
          <MilestoneFlagIcon size={flagPx} reached={isMilestoneReached} />
        </div>
      );
    }

    const nodeWidth = Math.max(dims.width * scale - shrink * 2, 3);
    const stripHeight = 1;
    const minH = primaryTagColor ? stripHeight + 2 : 2;
    const nodeHeight = Math.max(dims.height * scale - shrink * 2, minH);

    const stripSubdued =
      isComplete || isLocked || isExcluded || isBlocked;
    const stripStyle = primaryTagColor
      ? {
          height: stripHeight,
          background: primaryTagColor,
          // Stronger split: live path pops; complete / locked / dead / blocked recede
          filter: stripSubdued
            ? 'saturate(0.82) brightness(0.52) contrast(0.92)'
            : 'saturate(1.48) brightness(1.2) contrast(1.08)',
          opacity: stripSubdued ? 0.38 : 1,
        }
      : null;
    const bodyBackground = getBodyStyle({
      isComplete,
      isExcluded,
      isLocked,
      isBlocked,
      isActive,
      isAvailable,
    });

    const classNames = [
      'flowchart-minimap__node',
      stripSubdued && 'flowchart-minimap__node--subdued',
      primaryTagColor && 'flowchart-minimap__node--has-primary-tag',
      isComplete && 'flowchart-minimap__node--complete',
      isAvailable && 'flowchart-minimap__node--available',
      isActive && 'flowchart-minimap__node--active',
      isLocked && 'flowchart-minimap__node--locked',
      isExcluded && 'flowchart-minimap__node--excluded',
      isBlocked && 'flowchart-minimap__node--blocked',
      task.nodeType === 'decision' && 'flowchart-minimap__node--decision',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div
        className={classNames}
        style={{
          transform: `translate(${miniX}px, ${miniY}px)`,
          width: nodeWidth,
          height: nodeHeight,
          background: bodyBackground,
        }}
      >
        {primaryTagColor && (
          <div
            className="flowchart-minimap__node-strip"
            style={stripStyle}
          />
        )}
        <div className="flowchart-minimap__node-body" />
      </div>
    );
  }
);

MiniNode.displayName = 'MiniNode';

export default MiniNode;
